// src/cockpit4/components/ChurnWarningPanel.tsx
// ============================================
// 流失预警与智能挽回（客户中心 P2-功能8）- v2.0 详细改进计划
// 流失风险信号识别 / 流失概率预测 / 自动挽回流程 / 挽回方案 / 效果追踪
// 数据来源：localStorage（xeaos_churn_panel）
// ============================================

import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { AlertTriangle, UserX, HeartHandshake, PhoneCall, Mail } from "lucide-react";

interface ChurnCustomer {
  id: string;
  name: string;
  riskScore: number;
  riskLevel: "高" | "中" | "低";
  signals: string[];
  value: number;
  lastActiveDays: number;
  stage: string;
  plan: string;
  status: "待处理" | "挽回中" | "已挽回";
}

const INITIAL: ChurnCustomer[] = [
  { id: "c1", name: "陈总（地产集团）", riskScore: 86, riskLevel: "高", signals: ["活跃度下降 72%", "竞品接触", "付款延迟 15 天"], value: 15800, lastActiveDays: 35, stage: "方案阶段", plan: "专属优惠 + 总经理回访", status: "待处理" },
  { id: "c2", name: "周老板（零售门店）", riskScore: 74, riskLevel: "高", signals: ["投诉增加 2 次", "服务使用下降"], value: 4200, lastActiveDays: 72, stage: "使用阶段", plan: "问题解决 + 补偿方案", status: "挽回中" },
  { id: "c3", name: "林经理（餐饮连锁）", riskScore: 62, riskLevel: "中", signals: ["活跃度下降 40%"], value: 9600, lastActiveDays: 18, stage: "报价阶段", plan: "价值重述 + 案例分享", status: "待处理" },
  { id: "c4", name: "孙总（制造业）", riskScore: 45, riskLevel: "低", signals: ["回款周期变长"], value: 32000, lastActiveDays: 9, stage: "续约阶段", plan: "续约优惠 + 增值服务", status: "已挽回" },
  { id: "c5", name: "何经理（连锁酒店）", riskScore: 58, riskLevel: "中", signals: ["负责人变更", "询价竞品"], value: 7800, lastActiveDays: 22, stage: "使用阶段", plan: "新人培训 + 交接支持", status: "挽回中" },
];

const KEY = "xeaos_churn_panel";

function load(): ChurnCustomer[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) return JSON.parse(raw) as ChurnCustomer[];
  } catch { /* ignore */ }
  return INITIAL;
}

export default function ChurnWarningPanel() {
  const [customers, setCustomers] = useState<ChurnCustomer[]>(load);

  const stats = useMemo(() => {
    const high = customers.filter((c) => c.riskLevel === "高").length;
    const pending = customers.filter((c) => c.status === "待处理").length;
    const recovered = customers.filter((c) => c.status === "已挽回").length;
    const totalValue = customers.filter((c) => c.riskLevel !== "低").reduce((a, c) => a + c.value, 0);
    return { high, pending, recovered, totalValue };
  }, [customers]);

  const sorted = useMemo(() => [...customers].sort((a, b) => b.riskScore - a.riskScore), [customers]);

  const startRecovery = (id: string) => {
    setCustomers((prev) => {
      const next = prev.map((c) => (c.id === id ? { ...c, status: "挽回中" as const } : c));
      try { localStorage.setItem(KEY, JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <AlertTriangle size={14} className="text-red-400" />
        <h3 className="text-[12px] font-medium text-white">流失预警与智能挽回</h3>
        <span className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">P2-功能8 · Churn Warning</span>
      </div>

      <div className="grid grid-cols-4 gap-2">
        <MetricCard label="高风险客户" value={`${stats.high} 人`} color="#F87171" />
        <MetricCard label="待挽回" value={`${stats.pending} 人`} color="#FBBF24" />
        <MetricCard label="已挽回" value={`${stats.recovered} 人`} color="#34D399" />
        <MetricCard label="受影响价值" value={`¥${stats.totalValue.toLocaleString()}`} color="#38BDF8" />
      </div>

      <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
        <div className="flex items-center gap-2 mb-2">
          <UserX size={11} className="text-red-400" />
          <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">流失风险客户 · 按风险分排序</span>
        </div>
        <div className="space-y-1.5">
          {sorted.map((c) => (
            <div key={c.id} className={`rounded-md border p-2.5 ${c.status === "已挽回" ? "border-emerald-500/20 bg-emerald-500/[0.03]" : "border-white/[0.04] bg-white/[0.01]"}`}>
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-medium text-white">{c.name}</span>
                <span className={`text-[9px] font-mono px-1.5 py-0.5 rounded ${
                  c.riskLevel === "高" ? "bg-red-500/15 text-red-400" : c.riskLevel === "中" ? "bg-amber-500/15 text-amber-400" : "bg-emerald-500/15 text-emerald-400"
                }`}>{c.riskLevel}风险</span>
                <span className="text-[9px] font-mono text-gray-600">{c.lastActiveDays}天未活跃 · {c.stage}</span>
                <span className={`ml-auto text-[9px] font-mono px-1.5 py-0.5 rounded ${
                  c.status === "待处理" ? "bg-amber-500/15 text-amber-400" : c.status === "挽回中" ? "bg-sky-500/15 text-sky-400" : "bg-emerald-500/15 text-emerald-400"
                }`}>{c.status}</span>
              </div>
              <div className="flex items-center gap-2 mt-1.5">
                <div className="flex-1 h-1.5 bg-white/10 rounded-full overflow-hidden">
                  <div className="h-full rounded-full transition-all" style={{ width: `${c.riskScore}%`, background: c.riskScore >= 70 ? "#F87171" : c.riskScore >= 50 ? "#FBBF24" : "#34D399" }} />
                </div>
                <span className="text-[9px] font-mono text-gray-400">{c.riskScore}分</span>
              </div>
              <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                {c.signals.map((s, i) => (
                  <span key={i} className="text-[8px] font-mono px-1.5 py-0.5 rounded bg-red-500/10 text-red-300/80">{s}</span>
                ))}
                <span className="text-[8px] font-mono px-1.5 py-0.5 rounded bg-cyan-500/10 text-cyan-300/80 ml-auto">挽回方案：{c.plan}</span>
              </div>
              {c.status !== "已挽回" && (
                <div className="flex items-center gap-1.5 mt-2">
                  <button onClick={() => startRecovery(c.id)} className="flex items-center gap-1 px-2 py-1 rounded-md border border-cyan-500/30 bg-cyan-500/10 text-[9px] font-mono text-cyan-300 hover:bg-cyan-500/20">
                    <PhoneCall size={9} /> 启动挽回
                  </button>
                  <button className="flex items-center gap-1 px-2 py-1 rounded-md border border-white/[0.06] bg-white/[0.02] text-[9px] font-mono text-gray-400 hover:text-white">
                    <Mail size={9} /> AI 生成挽回邮件
                  </button>
                  <span className="ml-auto text-[8px] font-mono text-gray-600">流失概率 {(c.riskScore / 1.2).toFixed(0)}%</span>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
        <div className="flex items-center gap-2 mb-2">
          <HeartHandshake size={11} className="text-emerald-400" />
          <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">挽回效果追踪</span>
        </div>
        <div className="grid grid-cols-3 gap-2 text-[9px] font-mono">
          {[
            { color: "#34D399", text: "本月挽回成功率 68%（行业均值 45%）" },
            { color: "#FBBF24", text: "高价值客户优先挽回 → 挽回带来的收入提升 2.3x" },
            { color: "#38BDF8", text: "流失主因：活跃度下降（52%）> 竞品接触（28%）> 价格（20%）" },
          ].map((s, i) => (
            <div key={i} className="flex items-start gap-1.5 rounded-md border border-white/[0.04] bg-white/[0.01] p-2">
              <span className="w-1.5 h-1.5 rounded-full mt-1 flex-shrink-0" style={{ background: s.color }} />
              <span className="text-gray-400 leading-relaxed">{s.text}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function MetricCard({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="rounded-lg border border-white/[0.06] bg-white/[0.02] px-3 py-2">
      <div className="text-[9px] font-mono text-gray-500 uppercase">{label}</div>
      <div className="text-sm font-semibold font-mono mt-0.5" style={{ color }}>{value}</div>
    </motion.div>
  );
}

